import axios from 'axios';
import {
  ChatInput,
  ChatResponse,
  WorkflowExecuteResult,
  NodeTemplate,
  ListWorkflowsRequest,
  ListWorkflowsResponse,
  GetWorkflowResponse,
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  SaveWorkflowRequest,
  SaveWorkflowResponse,
  PublishWorkflowRequest,
  PublishWorkflowResponse,
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  StartExecutionRequest,
  StartExecutionResponse,
  ExecuteNodeRequest,
  ExecuteNodeResponse,
  ListExecutionByWorkflowRequest,
  ListExecutionByWorkflowResponse,
  ListWorkflowExecutionsRequest,
  ListWorkflowExecutionsResponse,
  ListWorkflowTestTasksRequest,
  ListWorkflowTestTasksResponse,
  CloneWorkflowRequest,
  CloneWorkflowResponse,
  GetProtocolWorkflowListRequest,
  GetProtocolWorkflowListResponse,
  ListNodeTemplatesResponse,
  GetExecutionDetailRequest,
  GetExecutionDetailResponse,
  GetWorkflowTemplateRequest,
  GetWorkflowTemplateResponse,
  BatchGetLatestVersionsRequest,
  BatchGetLatestVersionsResponse,
} from '../types/workflow';

/**
 * 获取工作流列表
 * @param data 请求参数
 */
export const listWorkflowsApi = (data: ListWorkflowsRequest) => {
  return axios.post<ListWorkflowsResponse>(
    '/api/console/workflow/v1/list',
    data
  );
};

/**
 * 获取工作空间内已发布版本的工作流列表（每个工作流一条，附带最新发布版本信息）
 */
export const getProtocolWorkflowListApi = (
  data: GetProtocolWorkflowListRequest
) => {
  return axios.post<GetProtocolWorkflowListResponse>(
    '/api/console/workflow/v1/history/list_published_workflows',
    data
  );
};

/**
 * 获取工作流详情
 * @param data 请求参数
 */
export const getWorkflowApi = (data: {
  tenant_uuid: string;
  workspace_uuid: string;
  workflow_uuid: string;
}) => {
  return axios.post<GetWorkflowResponse>('/api/console/workflow/v1/get', data);
};

/**
 * 创建工作流
 * @param data 工作流创建参数
 */
export const createWorkflowApi = (data: CreateWorkflowRequest) => {
  return axios.post<CreateWorkflowResponse>(
    '/api/console/workflow/v1/create',
    data
  );
};

/**
 * 保存工作流定义
 * @param data 工作流配置
 */
export const saveWorkflowApi = (data: SaveWorkflowRequest) => {
  return axios.post<SaveWorkflowResponse>(
    '/api/console/workflow/v1/save',
    data
  );
};

/**
 * 删除工作流
 * @param data 请求参数
 */
export const deleteWorkflowApi = (data: {
  tenant_uuid: string;
  workspace_uuid: string;
  workflow_uuid: string;
}) => {
  return axios.post<object>('/api/console/workflow/v1/delete', data);
};

/**
 * 发布工作流到执行标识
 * @param workflowId 工作流ID
 * @param data 发布参数
 */
export const publishWorkflowApi = (
  workflowId: string,
  data: PublishWorkflowRequest
) => {
  return axios.post<PublishWorkflowResponse>(
    `/api/workflow/${workflowId}/publish`,
    data
  );
};

/**
 * 异步启动工作流执行
 * @param data 请求参数
 */
export const startExecutionApi = (data: StartExecutionRequest) => {
  return axios.post<StartExecutionResponse>(
    '/api/console/workflow/v1/execution/start',
    data
  );
};

/**
 * 执行工作流
 * @param data 执行参数
 */
export const executeWorkflowApi = (data: ExecuteWorkflowRequest) => {
  return axios.post<ExecuteWorkflowResponse>(
    '/api/console/workflow/v1/execute',
    data
  );
};

/**
 * 执行工作流节点
 * @param data 执行参数
 */
export const executeNodeApi = (data: ExecuteNodeRequest) => {
  return axios.post<ExecuteNodeResponse>(
    '/api/console/workflow/v1/execute_node',
    data
  );
};

/**
 * 通过执行标识执行工作流
 * @param protocol 执行标识名称
 * @param data 输入数据
 */
export const executeByProtocolApi = (
  protocol: string,
  data: Record<string, any>
) => {
  return axios.post<WorkflowExecuteResult>(
    `/api/workflow/protocol/${protocol}`,
    data
  ) as unknown as Promise<WorkflowExecuteResult>;
};

/**
 * 执行闲聊对话
 * @param data 对话参数
 */
export const chatConversationApi = (data: ChatInput) => {
  return axios.post<ChatResponse>(
    '/api/workflow/protocol/chat_bot',
    data
  ) as unknown as Promise<ChatResponse>;
};

/**
 * 流式执行闲聊对话
 * @param params 对话参数
 */
export const streamChatConversationApi = (params: ChatInput) => {
  return axios({
    method: 'post',
    url: '/api/workflow/protocol/chat_bot',
    data: params,
    responseType: 'stream',
  });
};

export const listExecutionByWorkflowApi = (
  data: ListExecutionByWorkflowRequest
) => {
  return axios.post<ListExecutionByWorkflowResponse>(
    '/api/console/workflow/v1/execution/list_newest_by_workflow/v2',
    data
  );
};

/**
 * 获取工作流执行记录列表
 * @param data 查询参数
 */
export const listWorkflowExecutionsApi = (
  data: ListWorkflowExecutionsRequest
) => {
  return axios.post<ListWorkflowExecutionsResponse>(
    '/api/console/workflow/v1/execution/list/v2',
    data
  );
};

/**
 * 按工作流查询 Test Tasks 列表
 * @param data 查询参数
 */
export const listWorkflowTestTasksApi = (
  data: ListWorkflowTestTasksRequest
) => {
  return axios.post<ListWorkflowTestTasksResponse>(
    '/api/console/workflow/v1/execution/history/list',
    data
  );
};

/**
 * 复制工作流
 * @param data 复制参数
 */
export const cloneWorkflowApi = (data: CloneWorkflowRequest) => {
  return axios.post<CloneWorkflowResponse>(
    '/api/console/workflow/v1/clone',
    data
  );
};

export const publishNodeTemplateApi = (data: {
  title: string;
  author: string;
  config: Record<string, any>;
  group_type: string;
  node_type: string;
  project_id: number;
}) => {
  return axios.post<NodeTemplate>(
    '/api/workflow/node/config/save',
    data
  ) as unknown as Promise<NodeTemplate>;
};

export const getNodeTemplateApi = (id: string) => {
  return axios.get<NodeTemplate>(
    `/api/workflow/node/config/${id}/info`
  ) as unknown as Promise<NodeTemplate>;
};

export const listNodeTemplatesApi = (data: {
  title: string;
  author: string;
  node_type: string;
  page: number;
  page_size: number;
}) => {
  return axios.get<ListNodeTemplatesResponse>(
    '/api/workflow/node/config/list',
    {
      params: {
        page_no: data.page,
        page_size: data.page_size,
        title: data.title,
        author: data.author,
        node_type: data.node_type,
      },
    }
  );
};

// 上传流程示例
export const uploadFlowExampleApi = (data: FormData) => {
  return axios.post('/api/workflow/flow/example/upload', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// 修改流程示例
export const updateFlowExampleApi = (
  id: string,
  data: { title: string; config: any }
) => {
  return axios.post(`/api/workflow/flow/example/${id}/update`, data);
};

// 获取流程示例详情
export const getFlowExampleApi = (id: string) => {
  return axios.get(`/api/workflow/flow/example/${id}/info`);
};

// 删除流程示例
export const deleteFlowExampleApi = (id: string) => {
  return axios.delete(`/api/workflow/flow/example/${id}/delete`);
};

// 获取示例类型列表
export const getFlowExampleCatalogListApi = (params?: {
  type?: 'workflow' | 'node';
}) => {
  return axios.get('/api/workflow/flow/example/catalog/list', { params });
};

// 列出流程示例
export const listFlowExamplesApi = (params?: {
  page?: number;
  page_size?: number;
  title?: string;
  catalog?: string;
  type?: 'workflow' | 'node';
  editor?: string;
}) => {
  return axios.get('/api/workflow/flow/example/list', { params });
};

// 保存为流程示例
export const saveFlowExampleApi = (data: {
  title: string;
  config: any;
  project_id: number;
  editor: string;
  catalog: string;
  type: 'workflow' | 'node';
  workflow_id?: string;
}) => {
  return axios.post('/api/workflow/flow/example/save', data);
};

export const clearAgentSessionApi = (workflowId: string) => {
  // 暂时使用创建新会话来清空
  return axios.get(`/api/workflow/copilot/session/${workflowId}`);
};

export const chatWithAgentApi = (
  workflowId: string,
  data: {
    message: string;
    context?: Record<string, any>;
  }
) => {
  // 使用 copilot chat 接口
  return axios.post('/api/workflow/copilot/chat', {
    ...data,
    workflow_id: workflowId,
  });
};

// 确认执行计划
export const agentConfirmPlanApi = (
  workflowId: string,
  data: { plan_id: string; confirmed: boolean }
) => {
  // 使用 copilot 执行计划接口
  return axios.post('/api/workflow/copilot/execute', {
    ...data,
    workflow_id: workflowId,
  });
};

// 回答澄清问题
export const agentAnswerClarifyApi = (
  workflowId: string,
  data: { answers: Record<string, string> }
) => {
  // 使用 copilot chat 接口传递澄清信息
  return axios.post('/api/workflow/agent/chat', {
    ...data,
    workflow_id: workflowId,
    type: 'clarify',
  });
};

// 分析删除节点的影响
export interface DeleteImpactResult {
  node_id: string;
  node_name: string;
  affected_nodes: {
    node_id: string;
    node_name: string;
    affected_vars: string[];
    can_reconnect: boolean;
    reconnect_to: string;
  }[];
  broken_edges: string[];
  orphaned_nodes: string[];
  can_auto_fix: boolean;
  auto_fix_suggests: {
    type: string;
    description: string;
    target_node: string;
    action: string;
  }[];
  warnings: string[];
}

export const agentAnalyzeDeleteImpactApi = (data: {
  workflow_id: string;
  node_id: string;
}) => {
  return axios.post<DeleteImpactResult>(
    '/api/workflow/agent/node/analyze-delete',
    data
  );
};

// AI 生成测试数据
export const generateTestDataApi = (data: {
  workflow_id: string;
  user_intent?: string;
}) => {
  return axios.post<Record<string, any>>(
    '/api/workflow/agent/generate-test-data',
    data
  );
};

/**
 * 获取执行详情 V2（按 execution_id）
 * @param data 请求参数
 */
export const getExecutionDetailByIdApi = (data: GetExecutionDetailRequest) => {
  return axios.post<GetExecutionDetailResponse>(
    '/api/console/workflow/v1/execution/detail/v2',
    data
  );
};

/**
 * 获取工作流模板
 * @param data 请求参数
 */
export const getWorkflowTemplateApi = (data: GetWorkflowTemplateRequest) => {
  return axios.post<GetWorkflowTemplateResponse>(
    '/api/console/workflow/v1/template',
    data
  );
};

/**
 * 批量获取工作流最新版本
 */
export const batchGetLatestVersionsApi = (
  data: BatchGetLatestVersionsRequest
) => {
  return axios.post<BatchGetLatestVersionsResponse>(
    '/api/console/workflow/v1/history/batchgetlatestversions',
    data
  );
};

export default {
  createWorkflowApi,
  saveWorkflowApi,
  publishWorkflowApi,
  executeWorkflowApi,
  executeByProtocolApi,
  chatConversationApi,
  streamChatConversationApi,
  listWorkflowExecutionsApi,
  listWorkflowTestTasksApi,
  uploadFlowExampleApi,
  getFlowExampleApi,
  deleteFlowExampleApi,
  listFlowExamplesApi,
  saveFlowExampleApi,
  // AI Agent exports
  clearAgentSessionApi,
  chatWithAgentApi,
  agentAnalyzeDeleteImpactApi,
  // 工作流历史接口
  getExecutionDetailByIdApi,
  getWorkflowTemplateApi,
  batchGetLatestVersionsApi,
};
